import { useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  FileText,
  Bot,
  User,
  FolderOpen,
  BookOpen,
  X,
  MessageSquarePlus,
  FileUp,
  Layers,
  Mail,
  FileSignature,
  PanelLeftClose,
  PanelLeftOpen,
  Wallet,
  Gift,
  DollarSign,
} from "lucide-react";
import { useAuth } from "../hooks/useAuth";
import { useSidebarStore } from "../stores/sidebarStore";

export default function Sidebar() {
  const { user } = useAuth();
  const location = useLocation();
  const isCollapsed = useSidebarStore((state) => state.isCollapsed);
  const isMobileOpen = useSidebarStore((state) => state.isMobileOpen);
  const closeMobile = useSidebarStore((state) => state.closeMobile);
  const toggle = useSidebarStore((state) => state.toggle);

  const isAdmin = user?.role === "admin";
  const dashboardPath = isAdmin ? "/dashboard" : "/home";
  const dashboardLabel = isAdmin ? "Dashboard" : "Home";

  const shouldShowLabels = !isCollapsed || isMobileOpen;

  const alignmentClass = shouldShowLabels ? "px-4" : "justify-center px-2";

  const baseLinkClass =
    "group relative flex items-center gap-3 rounded-xl py-3 transition-all duration-300 overflow-hidden";
  const activeClass =
    "bg-indigo-50/80 text-indigo-700 font-bold shadow-sm before:absolute before:left-0 before:top-2 before:bottom-2 before:w-1.5 before:bg-indigo-600 before:rounded-r-full";
  const inactiveClass =
    "text-gray-600 hover:bg-indigo-50/60 hover:text-indigo-600 font-medium";
  const desktopWidthClass = isCollapsed ? "md:w-20" : "md:w-72";

  useEffect(() => {
    closeMobile();
  }, [location.pathname, closeMobile]);

  const handleNavClick = () => {
    closeMobile();
  };

  return (
    <>
      {/* Mobile Overlay */}
      <div
        className={`fixed inset-0 z-40 bg-gray-900/40 backdrop-blur-sm transition-opacity duration-300 md:hidden ${
          isMobileOpen ? "opacity-100" : "pointer-events-none opacity-0"
        }`}
        onClick={closeMobile}
      />

      <aside
        className={`fixed left-0 top-0 z-50 h-screen ${desktopWidthClass} flex flex-col border-r border-gray-100 bg-white shadow-sm transition-all duration-300 ease-in-out ${
          isMobileOpen ? "translate-x-0" : "-translate-x-full"
        } md:translate-x-0 overflow-hidden`}
      >
        {/* WATERMARK LOGO */}
        <img
          src="/resumeX.svg"
          alt=""
          className={`absolute -bottom-8 -left-10 z-0 w-[1000px] pointer-events-none select-none transition-all duration-500 ${
            shouldShowLabels ? "opacity-[0.15] scale-100" : "opacity-0 scale-95"
          }`}
        />

        {/* Header Section */}
        <div
          className={`relative z-10 flex h-[72px] items-center border-b border-gray-100/50 ${
            shouldShowLabels ? "justify-between px-6" : "justify-center"
          }`}
        >
          {shouldShowLabels && (
            <Link
              to={dashboardPath}
              onClick={handleNavClick}
              className="group flex items-center truncate"
            >
              <span className="text-2xl font-black tracking-tight text-gray-900">
                Resume
                <span className="text-indigo-600 transition-colors duration-300 group-hover:text-indigo-500">
                  X
                </span>
              </span>
            </Link>
          )}

          {/* Mobile Close Button */}
          <button
            onClick={closeMobile}
            className="absolute right-4 z-20 rounded-xl p-2 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-900 md:hidden"
            aria-label="Close sidebar"
          >
            <X className="h-5 w-5" />
          </button>

          {/* Desktop manual collapse toggle */}
          <button
            onClick={toggle}
            aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
            title={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
            className="hidden md:flex items-center justify-center rounded-xl p-2 text-gray-500 transition-colors hover:bg-gray-100 hover:text-indigo-600"
          >
            {isCollapsed ? (
              <PanelLeftOpen className="h-5 w-5" />
            ) : (
              <PanelLeftClose className="h-5 w-5" />
            )}
          </button>
        </div>

        {/* Navigation Section */}
        <nav className="relative z-10 flex flex-1 flex-col overflow-y-auto overflow-x-hidden p-3 scrollbar-hide [&::-webkit-scrollbar]:hidden">
          <div className="space-y-1.5">
            <NavLink
              to={dashboardPath}
              onClick={handleNavClick}
              className={({ isActive }) =>
                `${baseLinkClass} ${alignmentClass} ${isActive ? activeClass : inactiveClass}`
              }
              title={dashboardLabel}
              data-tour="dashboard"
            >
              <LayoutDashboard className="h-5 w-5 flex-shrink-0 transition-transform duration-300 group-hover:scale-110" />
              {shouldShowLabels && (
                <span className="whitespace-nowrap transition-transform duration-300 group-hover:translate-x-1">
                  {dashboardLabel}
                </span>
              )}
            </NavLink>

            {isAdmin && (
              <NavLink
                to="/admin/blog/new"
                onClick={handleNavClick}
                className={({ isActive }) =>
                  `${baseLinkClass} ${alignmentClass} ${isActive ? activeClass : inactiveClass}`
                }
                title="Create Blog Post"
                data-tour="create-post"
              >
                <FileText className="h-5 w-5 flex-shrink-0 transition-transform duration-300 group-hover:scale-110" />
                {shouldShowLabels && (
                  <span className="whitespace-nowrap transition-transform duration-300 group-hover:translate-x-1">
                    Create Post
                  </span>
                )}
              </NavLink>
            )}

            {/* explicitly checking for /editor in the URL so it stays active! */}
            <NavLink
              to="/cv-builder"
              onClick={handleNavClick}
              className={({ isActive }) =>
                `${baseLinkClass} ${alignmentClass} ${
                  isActive || location.pathname.startsWith("/editor")
                    ? activeClass
                    : inactiveClass
                }`
              }
              title="Resume Builder"
              data-tour="cv-builder"
            >
              <FileText className="h-5 w-5 flex-shrink-0 transition-transform duration-300 group-hover:scale-110" />
              {shouldShowLabels && (
                <span className="whitespace-nowrap transition-transform duration-300 group-hover:translate-x-1">
                  Resume Builder
                </span>
              )}
            </NavLink>

            <NavLink
              to="/ai-assistant"
              onClick={handleNavClick}
              className={({ isActive }) =>
                `${baseLinkClass} ${alignmentClass} ${isActive ? activeClass : inactiveClass}`
              }
              title="AI Assistant"
              data-tour="ai-assistant"
            >
              <Bot className="h-5 w-5 flex-shrink-0 transition-transform duration-300 group-hover:scale-110" />
              {shouldShowLabels && (
                <span className="whitespace-nowrap transition-transform duration-300 group-hover:translate-x-1">
                  AI Assistant
                </span>
              )}
            </NavLink>

            <NavLink
              to="/cover-letter"
              onClick={handleNavClick}
              className={({ isActive }) =>
                `${baseLinkClass} ${alignmentClass} ${isActive ? activeClass : inactiveClass}`
              }
              title="Cover Letter"
              data-tour="cover-letter"
            >
              <FileSignature className="h-5 w-5 flex-shrink-0 transition-transform duration-300 group-hover:scale-110" />
              {shouldShowLabels && (
                <span className="whitespace-nowrap transition-transform duration-300 group-hover:translate-x-1">
                  Cover Letter
                </span>
              )}
            </NavLink>

            <NavLink
              to="/my-resumes"
              onClick={handleNavClick}
              className={({ isActive }) =>
                `${baseLinkClass} ${alignmentClass} ${isActive ? activeClass : inactiveClass}`
              }
              title="My Resumes"
              data-tour="my-resumes"
            >
              <FolderOpen className="h-5 w-5 flex-shrink-0 transition-transform duration-300 group-hover:scale-110" />
              {shouldShowLabels && (
                <span className="whitespace-nowrap transition-transform duration-300 group-hover:translate-x-1">
                  My Resumes
                </span>
              )}
            </NavLink>

            <NavLink
              to="/templates"
              onClick={handleNavClick}
              className={({ isActive }) =>
                `${baseLinkClass} ${alignmentClass} ${isActive ? activeClass : inactiveClass}`
              }
              title="Templates"
              data-tour="templates"
            >
              <Layers className="h-5 w-5 flex-shrink-0 transition-transform duration-300 group-hover:scale-110" />
              {shouldShowLabels && (
                <span className="whitespace-nowrap transition-transform duration-300 group-hover:translate-x-1">
                  Templates
                </span>
              )}
            </NavLink>

            {isAdmin && (
              <NavLink
                to="/admin/templates"
                onClick={handleNavClick}
                className={({ isActive }) =>
                  `${baseLinkClass} ${alignmentClass} ${isActive ? activeClass : inactiveClass}`
                }
                title="Templates Management"
              >
                <Layers className="h-5 w-5 flex-shrink-0 transition-transform duration-300 group-hover:scale-110" />
                {shouldShowLabels && (
                  <span className="whitespace-nowrap transition-transform duration-300 group-hover:translate-x-1">
                    Templates Management
                  </span>
                )}
              </NavLink>
            )}

            {isAdmin && (
              <NavLink
                to="/admin/pricing"
                onClick={handleNavClick}
                className={({ isActive }) =>
                  `${baseLinkClass} ${alignmentClass} ${isActive ? activeClass : inactiveClass}`
                }
                title="Pricing Plans"
              >
                <DollarSign className="h-5 w-5 flex-shrink-0 transition-transform duration-300 group-hover:scale-110" />
                {shouldShowLabels && (
                  <span className="whitespace-nowrap transition-transform duration-300 group-hover:translate-x-1">
                    Pricing Plans
                  </span>
                )}
              </NavLink>
            )}

            {isAdmin && (
              <NavLink
                to="/admin/newsletter"
                onClick={handleNavClick}
                className={({ isActive }) =>
                  `${baseLinkClass} ${alignmentClass} ${isActive ? activeClass : inactiveClass}`
                }
                title="Newsletter"
              >
                <Mail className="h-5 w-5 flex-shrink-0 transition-transform duration-300 group-hover:scale-110" />
                {shouldShowLabels && (
                  <span className="whitespace-nowrap transition-transform duration-300 group-hover:translate-x-1">
                    Newsletter
                  </span>
                )}
              </NavLink>
            )}

            {isAdmin && (
              <NavLink
                to="/admin/offers"
                onClick={handleNavClick}
                className={({ isActive }) =>
                  `${baseLinkClass} ${alignmentClass} ${isActive ? activeClass : inactiveClass}`
                }
                title="Offers"
              >
                <Gift className="h-5 w-5 flex-shrink-0 transition-transform duration-300 group-hover:scale-110" />
                {shouldShowLabels && (
                  <span className="whitespace-nowrap transition-transform duration-300 group-hover:translate-x-1">
                    Offers
                  </span>
                )}
              </NavLink>
            )}

            {isAdmin && (
              <NavLink
                to="/admin/user-cvs"
                onClick={handleNavClick}
                className={({ isActive }) =>
                  `${baseLinkClass} ${alignmentClass} ${isActive ? activeClass : inactiveClass}`
                }
                title="Users CVs"
              >
                <FileUp className="h-5 w-5 flex-shrink-0 transition-transform duration-300 group-hover:scale-110" />
                {shouldShowLabels && (
                  <span className="whitespace-nowrap transition-transform duration-300 group-hover:translate-x-1">
                    Users CVs
                  </span>
                )}
              </NavLink>
            )}

            {isAdmin && (
              <NavLink
                to="/admin/finance"
                onClick={handleNavClick}
                className={({ isActive }) =>
                  `${baseLinkClass} ${alignmentClass} ${isActive ? activeClass : inactiveClass}`
                }
                title="Finance"
              >
                <Wallet className="h-5 w-5 flex-shrink-0 transition-transform duration-300 group-hover:scale-110" />
                {shouldShowLabels && (
                  <span className="whitespace-nowrap transition-transform duration-300 group-hover:translate-x-1">
                    Finance
                  </span>
                )}
              </NavLink>
            )}

            <NavLink
              to="/blog"
              onClick={handleNavClick}
              className={({ isActive }) =>
                `${baseLinkClass} ${alignmentClass} ${isActive ? activeClass : inactiveClass}`
              }
              title="Blog"
              data-tour="blog"
            >
              <BookOpen className="h-5 w-5 flex-shrink-0 transition-transform duration-300 group-hover:scale-110" />
              {shouldShowLabels && (
                <span className="whitespace-nowrap transition-transform duration-300 group-hover:translate-x-1">
                  Blog
                </span>
              )}
            </NavLink>

            <NavLink
              to="/profile"
              onClick={handleNavClick}
              className={({ isActive }) =>
                `${baseLinkClass} ${alignmentClass} ${isActive ? activeClass : inactiveClass}`
              }
              title="Profile"
              data-tour="profile"
            >
              <User className="h-5 w-5 flex-shrink-0 transition-transform duration-300 group-hover:scale-110" />
              {shouldShowLabels && (
                <span className="whitespace-nowrap transition-transform duration-300 group-hover:translate-x-1">
                  Profile
                </span>
              )}
            </NavLink>

            <NavLink
              to={isAdmin ? "/admin/feedback" : "/feedback"}
              onClick={handleNavClick}
              className={({ isActive }) =>
                `${baseLinkClass} ${alignmentClass} ${isActive ? activeClass : inactiveClass}`
              }
              title="Feedback"
              data-tour="feedback"
            >
              <MessageSquarePlus className="h-5 w-5 flex-shrink-0 transition-transform duration-300 group-hover:scale-110" />
              {shouldShowLabels && (
                <span className="whitespace-nowrap transition-transform duration-300 group-hover:translate-x-1">
                  Feedback
                </span>
              )}
            </NavLink>
          </div>
        </nav>

        {/* Version Footer — always fixed at the bottom */}
        <div className="relative z-10 h-14 border-t border-gray-300 flex items-center justify-center shrink-0">
          <span
            className={`absolute whitespace-nowrap text-[11px] uppercase tracking-wider text-gray-600 font-bold transition-all duration-300 ${shouldShowLabels ? "opacity-100 scale-100" : "opacity-0 scale-95 pointer-events-none"}`}
          >
            ResumeX <span className="text-gray-300 mx-1">|</span> v3.0.5
          </span>
          <span
            className={`absolute whitespace-nowrap text-[10px] font-bold text-gray-400 transition-all duration-300 ${!shouldShowLabels ? "opacity-100 scale-100" : "opacity-0 scale-95 pointer-events-none"}`}
          >
            v3.0.5
          </span>
        </div>
      </aside>
    </>
  );
}
